import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { Reveal } from "../components/Reveal";
import { CalendlyButton } from "../components/CalendlyButton";

/**
 * Shared premium split-layout biography page (bright theme).
 * props: seo, image, imageAlt, eyebrow, name, subheadline, intro (array of paragraphs),
 *        sections [{ title, type: 'list'|'pairs', items }], finalHeadline
 */
export function BioPage({
  seo, image, imageAlt, eyebrow, name, subheadline, intro, sections, finalHeadline, testid,
}) {
  useSeo(seo);

  return (
    <div data-testid={testid}>
      {/* Split hero */}
      <section className="relative overflow-hidden bg-warmwhite pt-28 md:pt-32" data-testid={`${testid}-hero`}>
        <div className="river-lines pointer-events-none absolute inset-0 opacity-70" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 pb-16 md:px-8 md:pb-20 lg:grid-cols-2 lg:gap-16">
          <Reveal className="order-2 flex flex-col justify-center lg:order-1">
            <p className="text-xs font-600 uppercase tracking-label text-gold">{eyebrow}</p>
            <h1 className="mt-4 font-serif text-5xl font-500 leading-[1.02] text-navy sm:text-6xl lg:text-7xl">
              {name}
            </h1>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-bluegray-text">{subheadline}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" data-testid={`${testid}-request`}
                className="group inline-flex items-center gap-2 rounded-sm bg-navy px-7 py-3.5 text-sm font-600 text-white transition-colors hover:bg-navy-light">
                Request a Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
              </Link>
              <CalendlyButton variant="outline" testid={`${testid}-calendly`} />
            </div>
          </Reveal>
          <Reveal delay={0.12} className="order-1 lg:order-2">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="absolute -inset-3 rounded-sm bg-gradient-to-br from-white to-cream" />
              <div className="absolute inset-0 -z-10 rounded-full bg-gold/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-sm rounded-tl-[3rem] border border-navy/10 bg-white">
                <img src={image} alt={imageAlt} className="h-[32rem] w-full object-cover object-top md:h-[36rem]" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="bg-warmwhite py-16 md:py-24" data-testid={`${testid}-body`}>
        <div className="mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-[1.5fr_1fr] lg:gap-20">
          {/* Biography */}
          <Reveal>
            <h2 className="font-serif text-3xl font-500 text-navy sm:text-4xl">Biography</h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-bluegray-text md:text-lg">
              {intro.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>

          {/* Structured sidebar */}
          <div className="space-y-10">
            {sections.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="rounded-sm border border-navy/10 bg-white p-7">
                  <h3 className="border-b border-gold/40 pb-3 font-serif text-2xl font-500 text-navy">{s.title}</h3>
                  {s.type === "pairs" ? (
                    <ul className="mt-4 space-y-4">
                      {s.items.map((it, j) => (
                        <li key={j}>
                          <p className="font-600 text-navy">{it.label}</p>
                          <p className="mt-0.5 text-sm leading-relaxed text-bluegray-text">{it.value}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="mt-4 space-y-2.5">
                      {s.items.map((it, j) => (
                        <li key={j} className="flex items-start gap-3 text-sm leading-relaxed text-bluegray-text">
                          <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rotate-45 bg-gold" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-cream py-20 md:py-24" data-testid={`${testid}-final-cta`}>
        <div className="arch-grid pointer-events-none absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-3xl px-5 text-center md:px-8">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-serif text-4xl font-500 leading-[1.05] text-navy sm:text-5xl">
              {finalHeadline}
            </h2>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to="/contact" data-testid={`${testid}-final-request`}
                className="group inline-flex items-center gap-2 rounded-sm bg-navy px-8 py-4 text-sm font-600 text-white transition-colors hover:bg-navy-light">
                Request Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
              </Link>
              <CalendlyButton testid={`${testid}-final-calendly`} />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
